import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Trophy, TrendingUp, Users, Activity, Zap, Clock } from "lucide-react";
import PhotoMissingWidget from "@/components/dashboard/PhotoMissingWidget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useDrillDown } from "@/components/drilldown/DrillDownContext";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import AISafetyCard from "@/components/AISafetyCard";
import Ranking3DExtended from "@/components/Ranking3DExtended";
import ActiveFiltersChips from "@/components/ActiveFiltersChips";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { useAuth } from "@/contexts/AuthContext";
import { colorFor, TX_COLORS } from "@/lib/reportColors";
import HelpTooltip from "@/components/HelpTooltip";

function KpiCard({ icon: Icon, label, value, sub, testId, accent = "#7AA2F7" }) {
  return (
    <Card
      data-testid={testId}
      className="overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-lg"
    >
      <CardContent className="relative p-5">
        <div
          className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-10"
          style={{ background: accent }}
        />
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {label}
            </div>
            <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
            {sub && <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>}
          </div>
          <div
            className="flex h-10 w-10 items-center justify-center rounded-md"
            style={{ background: `${accent}22`, color: accent }}
          >
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();
  const { resolveCanonicalId } = useDrillDown();
  const navigate = useNavigate();
  const { filters, setFilters, reset, payload } = useFilters();
  const [kpis, setKpis] = useState(null);
  const [rank, setRank] = useState([]);
  const [dist, setDist] = useState([]);
  const [trend, setTrend] = useState({ labels: [], series: [] });
  const [winner, setWinner] = useState(null);
  // Phase 7.3 — granular loading states so each card can render
  // independently. The previous global `kpis == null` ternary masked
  // the bug: a single slow endpoint blocked the entire render.
  const [kpisLoading, setKpisLoading] = useState(true);
  const [winnerLoading, setWinnerLoading] = useState(true);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  // Auth hydrates async; gate the fetch on a real authenticated user so the
  // effect re-runs once auth lands.
  const authReady = !!user && user !== false;
  const userId = user && user !== false ? user.id : null;

  // Phase 7.3 — Effect A: 4 fast endpoints (kpis / ranking / distribución /
  // tendencia), each fired independently with its own state setter. We do
  // NOT use Promise.allSettled here because a single slow endpoint would
  // delay every visible card until the last one settled. Each .then() is
  // guarded by `controller.signal.aborted` so React StrictMode's dev
  // double-mount can never apply state on an unmounted instance.
  useEffect(() => {
    if (!authReady) return undefined;
    const controller = new AbortController();
    const config = { signal: controller.signal };
    const safe = (fn) => (value) => {
      if (!controller.signal.aborted) fn(value);
    };

    setKpisLoading(true);
    const tasks = [];

    if (hasPermission("report:kpis")) {
      tasks.push(
        reports
          .kpis(payload, config)
          .then(({ data }) => safe(setKpis)(data))
          .catch(() => safe(setKpis)(null)),
      );
    } else {
      setKpis(null);
    }

    if (hasPermission("report:ranking")) {
      tasks.push(
        reports
          .ranking(payload, 10, config)
          .then(({ data }) => safe(setRank)(data?.items || []))
          .catch(() => safe(setRank)([])),
      );
    } else {
      setRank([]);
    }

    if (hasPermission("report:distribucion")) {
      tasks.push(
        reports
          .distribucion(payload, config)
          .then(({ data }) => safe(setDist)(data?.items || []))
          .catch(() => safe(setDist)([])),
      );
    } else {
      setDist([]);
    }

    if (hasPermission("report:tendencia")) {
      tasks.push(
        reports
          .tendencia(payload, "day", "transaction_type", config)
          .then(({ data }) => safe(setTrend)(data || { labels: [], series: [] }))
          .catch(() => safe(setTrend)({ labels: [], series: [] })),
      );
    } else {
      setTrend({ labels: [], series: [] });
    }

    // Loading flag flips off once ALL of Effect A settles. Each individual
    // card has already updated as soon as its own endpoint resolved, so
    // this only controls the global "skeletons → blank" transition.
    Promise.allSettled(tasks).finally(() => safe(setKpisLoading)(false));

    return () => controller.abort();
    // payload tracked via payloadKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, authReady, userId]);

  // Phase 7.3 — Effect B: slow `ganador-del-mes`. Isolated so its ~6 s cold
  // latency (now cached server-side to ~120 ms warm) never blocks the
  // primary KPIs. Has its own AbortController and its own loading flag.
  useEffect(() => {
    if (!authReady) return undefined;
    if (!hasPermission("report:ganador")) {
      setWinner(null);
      setWinnerLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setWinnerLoading(true);
    reports
      .ganador(payload, "month", null, "volume", { signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted) setWinner(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setWinner(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setWinnerLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, authReady, userId]);

  const trendData = trend.labels.map((lab, i) => {
    const row = { label: lab };
    trend.series.forEach((s) => (row[s.name] = s.data[i] || 0));
    return row;
  });

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="dashboard-title">
          {t("dashboard.title")}
        </h1>
        <HelpTooltip anchor="dashboard" label={t("help.tooltip.dashboard")} testId="help-tooltip-dashboard" />
        <p className="text-sm text-muted-foreground">{t("dashboard.subtitle")}</p>
      </div>

      <GlobalFilters
        filters={filters}
        onChange={setFilters}
        onReset={reset}
        storageKey="filters_panel_open:dashboard"
        testId="dashboard-filters-panel"
      />

      <ActiveFiltersChips
        filters={filters}
        onClear={(key, next) => {
          if (key === "__all__") {
            setFilters({
              employee_ids: [],
              department_ids: [],
              transaction_types: [],
            });
            return;
          }
          setFilters({ [key]: next });
        }}
        testId="dashboard-drilldown-chips"
      />

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5" data-testid="dashboard-kpis">
        <KpiCard
          icon={Activity}
          accent="#F7768E"
          label={t("dashboard.kpis.transactions")}
          value={kpis ? kpis.total_transactions.toLocaleString() : "—"}
          testId="kpi-total"
        />
        <KpiCard
          icon={Users}
          accent="#7DCFFF"
          label={t("dashboard.kpis.activeEmployees")}
          value={kpis ? kpis.total_employees_active : "—"}
          testId="kpi-active-emps"
        />
        <KpiCard
          icon={Trophy}
          accent="#E0AF68"
          label={t("dashboard.kpis.topEmployee")}
          value={kpis?.top_employee?.name || "—"}
          sub={
            kpis?.top_employee?.count
              ? `${kpis.top_employee.count.toLocaleString()} · ${kpis.top_employee.id}`
              : ""
          }
          testId="kpi-top-emp"
        />
        <KpiCard
          icon={TrendingUp}
          accent="#9ECE6A"
          label={t("dashboard.kpis.topType")}
          value={kpis?.top_transaction_type?.type || "—"}
          sub={
            kpis?.top_transaction_type?.count
              ? kpis.top_transaction_type.count.toLocaleString()
              : ""
          }
          testId="kpi-top-type"
        />
        {hasPermission("catalog:view") && <PhotoMissingWidget />}
      </div>

      {/* Efficiency KPIs (Phase 3.5) — only when TE350 hours are available. */}
      {kpis?.has_hours && (
        <div
          className="grid gap-4 md:grid-cols-2"
          data-testid="dashboard-kpis-efficiency"
        >
          <KpiCard
            icon={Clock}
            accent="#BB9AF7"
            label={t("dashboard.kpis.avgTxHr")}
            value={
              kpis.avg_tx_per_hour != null
                ? kpis.avg_tx_per_hour.toFixed(2)
                : "—"
            }
            sub={t("ganador.txPerHourLabel")}
            testId="kpi-avg-tx-hr"
          />
          <KpiCard
            icon={Zap}
            accent="#F7C56D"
            label={t("dashboard.kpis.topEfficient")}
            value={kpis.top_efficient_employee?.name || "—"}
            sub={
              kpis.top_efficient_employee
                ? `${kpis.top_efficient_employee.tx_per_hour} tx/hr · ${kpis.top_efficient_employee.total_hours} h`
                : ""
            }
            testId="kpi-top-efficient"
          />
        </div>
      )}

      {/* AI safety / productivity message — between KPIs and the rest. */}
      <AISafetyCard defaultCategory="safety" testIdPrefix="dashboard-ai-msg" />

      {/* Winner highlight */}
      {winner?.winner && (
        <Card data-testid="dashboard-winner" className="overflow-hidden">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <div className="min-w-0">
              <CardTitle className="text-base">{t("dashboard.winnerTitle")}</CardTitle>
              <p
                className="mt-0.5 text-xs text-muted-foreground"
                data-testid="dashboard-winner-subtitle"
              >
                {t("dashboard.winnerSubtitle")}
              </p>
            </div>
            <Link
              to="/reports/ganador"
              className="shrink-0 pl-2 text-xs text-muted-foreground hover:text-foreground"
              data-testid="winner-view-full-link"
            >
              {t("dashboard.viewFull")}
            </Link>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div
                className="flex h-16 w-16 items-center justify-center rounded-full text-2xl font-semibold text-white shadow-md"
                style={{ background: winner.winner.department_color || "#7AA2F7" }}
              >
                {winner.winner.photo_initial}
              </div>
              <div>
                <div className="text-xl font-semibold">{winner.winner.employee_name}</div>
                <div className="text-sm text-muted-foreground">
                  <Badge variant="outline" className="mr-2 text-xs">
                    {winner.winner.employee_id}
                  </Badge>
                  {winner.winner.department_name || ""} · {winner.period_label}
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-3xl font-semibold tabular-nums">
                  {winner.winner.total.toLocaleString()}
                </div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  {t("ganador.totalTransactions")}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3D Extended Ranking — hero visual under the winner card */}
      {hasPermission("report:ranking") && (
        <Ranking3DExtended
          payload={payload}
          defaultTop={12}
          selectedEmployeeId={(filters.employee_ids || [])[0] || null}
          onEmployeeClick={(item) => {
            const id = String(item.employee_id || "");
            const already = (filters.employee_ids || []).includes(id);
            setFilters({ employee_ids: already ? [] : [id] });
          }}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Ranking top 10 */}
        <Card data-testid="dashboard-ranking">
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.rankingTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            {rank.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {t("common.no_data")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={rank}
                  layout="vertical"
                  margin={{ left: 60 }}
                  onClick={(e) => {
                    // Phase 9.1 — click bar → open employee drill-down
                    const emp = e?.activePayload?.[0]?.payload;
                    const cid = emp && resolveCanonicalId(emp.employee_id);
                    if (cid) navigate(`/employees/${cid}`);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis type="number" />
                  <YAxis
                    type="category"
                    dataKey="employee_name"
                    width={120}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }}
                  />
                  <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                    {rank.map((r, i) => (
                      <Cell key={i} fill={r.department_color || colorFor(null, i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Distribution */}
        <Card data-testid="dashboard-distribution">
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.distributionTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            {dist.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {t("common.no_data")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={dist}
                    dataKey="count"
                    nameKey="transaction_type"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                  >
                    {dist.map((d, i) => (
                      <Cell
                        key={i}
                        fill={TX_COLORS[d.transaction_type] || colorFor(null, i)}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, n, p) =>
                      [`${v.toLocaleString()} (${p.payload.pct}%)`, n]
                    }
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Trend */}
      <Card data-testid="dashboard-trend">
        <CardHeader>
          <CardTitle className="text-base">{t("dashboard.trendTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          {trend.labels.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              {t("common.no_data")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }}
                />
                <Legend />
                {trend.series.map((s, i) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={TX_COLORS[s.name] || colorFor(null, i)}
                    strokeWidth={2}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
